// SNIGJ - Prisoner Roll Call Component
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ClipboardCheck, Users, CheckCircle, XCircle, AlertTriangle, 
  Fingerprint, Eye, Search, Clock, UserCheck, UserX, 
  Play, Square, RefreshCw, Loader2, ScanLine, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Prisoner status options during roll call
const ROLL_CALL_STATUSES = [
  { value: 'PRESENT', label: { fr: 'Présent', en: 'Present' }, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
  { value: 'ABSENT', label: { fr: 'Absent', en: 'Absent' }, icon: XCircle, color: 'text-red-600 bg-red-50' },
  { value: 'HOSPITAL', label: { fr: 'Hôpital', en: 'Hospital' }, icon: AlertTriangle, color: 'text-blue-600 bg-blue-50' },
  { value: 'COURT', label: { fr: 'Tribunal', en: 'Court' }, icon: Building2, color: 'text-purple-600 bg-purple-50' },
  { value: 'TRANSFER', label: { fr: 'Transfert', en: 'Transfer' }, icon: RefreshCw, color: 'text-orange-600 bg-orange-50' },
  { value: 'ESCAPED', label: { fr: 'Évadé', en: 'Escaped' }, icon: AlertTriangle, color: 'text-red-700 bg-red-100' },
];

// Verification methods
const VERIFICATION_METHODS = [
  { value: 'VISUAL', label: { fr: 'Visuel', en: 'Visual' }, icon: Eye },
  { value: 'FINGERPRINT', label: { fr: 'Empreinte', en: 'Fingerprint' }, icon: Fingerprint },
  { value: 'RETINAL', label: { fr: 'Rétinien', en: 'Retinal' }, icon: ScanLine },
  { value: 'BOTH_BIOMETRIC', label: { fr: 'Empreinte + Rétinien', en: 'Both Biometric' }, icon: UserCheck },
];

const PrisonerRollCall = ({ 
  open, 
  onClose, 
  prison,
  onComplete 
}) => {
  const { language } = useAuth();
  const [rollCall, setRollCall] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPrisoner, setCurrentPrisoner] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState('VISUAL');
  const [biometricScanning, setBiometricScanning] = useState(false);
  const [biometricResult, setBiometricResult] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  // Start roll call when modal opens
  useEffect(() => {
    if (open && prison && !rollCall) {
      startRollCall();
    }
    if (!open) {
      setRollCall(null);
      setCurrentPrisoner(null);
      setBiometricResult(null);
    }
  }, [open, prison]);

  const startRollCall = async () => {
    setLoading(true);
    try {
      const response = await api.post('/roll-call', {
        prison_id: prison.structure_id,
        roll_call_type: 'ROUTINE'
      });
      setRollCall(response.data);
      toast.success(language === 'fr' ? 'Appel démarré' : 'Roll call started');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const recordResponse = async (prisonerId, status) => {
    if (!rollCall) return;
    
    try {
      await api.post(`/roll-call/${rollCall.roll_call_id}/response`, {
        prisoner_id: prisonerId,
        status: status,
        verification_method: verificationMethod,
        notes: biometricResult?.verified ? `Vérifié biométrique (${biometricResult.confidence * 100}%)` : null
      });

      // Update local state
      setRollCall(prev => ({
        ...prev,
        prisoner_list: prev.prisoner_list.map(p => 
          p.prisoner_id === prisonerId 
            ? { ...p, status, verification_method: verificationMethod }
            : p
        ),
        present: prev.present + (status === 'PRESENT' ? 1 : 0),
        absent: prev.absent + (status === 'ABSENT' || status === 'ESCAPED' ? 1 : 0),
        verified_biometric: prev.verified_biometric + (verificationMethod !== 'VISUAL' ? 1 : 0)
      }));

      setCurrentPrisoner(null);
      setBiometricResult(null);
      toast.success(language === 'fr' ? 'Réponse enregistrée' : 'Response recorded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const verifyBiometric = async (prisonerId, biometricType) => {
    setBiometricScanning(true);
    setBiometricResult(null);

    try {
      // Simulate biometric capture delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const response = await api.post(`/roll-call/${rollCall.roll_call_id}/verify-biometric`, {
        prisoner_id: prisonerId,
        biometric_type: biometricType,
        biometric_data: 'simulated_scan_data_base64'
      });

      setBiometricResult(response.data);
      
      if (response.data.verified) {
        toast.success(
          language === 'fr' 
            ? `Identité vérifiée: ${response.data.prisoner_name}` 
            : `Identity verified: ${response.data.prisoner_name}`
        );
        setVerificationMethod(biometricType);
      } else {
        toast.error(response.data.error || (language === 'fr' ? 'Échec de la vérification' : 'Verification failed'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de vérification');
    } finally {
      setBiometricScanning(false);
    }
  };

  const completeRollCall = async () => {
    if (!rollCall) return;
    
    try {
      const response = await api.post(`/roll-call/${rollCall.roll_call_id}/complete`);
      toast.success(
        language === 'fr' 
          ? `Appel terminé - ${response.data.present} présents, ${response.data.absent} absents`
          : `Roll call complete - ${response.data.present} present, ${response.data.absent} absent`
      );
      onComplete?.(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Filter prisoners
  const getPrisonersByStatus = (status) => {
    if (!rollCall?.prisoner_list) return [];
    const filtered = rollCall.prisoner_list.filter(p => {
      const matchesStatus = status === 'all' ? true : 
        status === 'pending' ? p.status === 'PENDING' :
        status === 'present' ? p.status === 'PRESENT' :
        status === 'absent' ? ['ABSENT', 'ESCAPED', 'HOSPITAL', 'COURT', 'TRANSFER'].includes(p.status) :
        true;
      const matchesSearch = !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });
    return filtered;
  };

  const pendingCount = rollCall?.prisoner_list?.filter(p => p.status === 'PENDING').length || 0;
  const presentCount = rollCall?.prisoner_list?.filter(p => p.status === 'PRESENT').length || 0;
  const absentCount = rollCall?.prisoner_list?.filter(p => p.status !== 'PENDING' && p.status !== 'PRESENT').length || 0;
  const progressPercent = rollCall?.total_prisoners > 0 
    ? ((rollCall.total_prisoners - pendingCount) / rollCall.total_prisoners) * 100 
    : 0;

  if (!prison) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-primary" />
            {language === 'fr' ? 'Appel des Prisonniers' : 'Prisoner Roll Call'}
            <Badge variant="outline">{prison.name}</Badge>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-3">{language === 'fr' ? 'Démarrage de l\'appel...' : 'Starting roll call...'}</span>
          </div>
        ) : rollCall ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Progress Section */}
            <div className="space-y-4 pb-4 border-b">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <Users className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold">{rollCall.total_prisoners}</p>
                  <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Total' : 'Total'}</p>
                </div>
                <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-600">{presentCount}</p>
                  <p className="text-xs text-green-600">{language === 'fr' ? 'Présents' : 'Present'}</p>
                </div>
                <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <XCircle className="w-6 h-6 mx-auto text-red-600 mb-1" />
                  <p className="text-2xl font-bold text-red-600">{absentCount}</p>
                  <p className="text-xs text-red-600">{language === 'fr' ? 'Absents' : 'Absent'}</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <Clock className="w-6 h-6 mx-auto text-yellow-600 mb-1" />
                  <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
                  <p className="text-xs text-yellow-600">{language === 'fr' ? 'En attente' : 'Pending'}</p>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{language === 'fr' ? 'Progression' : 'Progress'}</span>
                  <span>{Math.round(progressPercent)}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>

              {/* Verification Method Selector */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">{language === 'fr' ? 'Méthode de vérification:' : 'Verification:'}</span>
                <div className="flex gap-2">
                  {VERIFICATION_METHODS.map(method => (
                    <Button
                      key={method.value}
                      variant={verificationMethod === method.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setVerificationMethod(method.value)}
                      className="gap-1"
                    >
                      <method.icon className="w-4 h-4" />
                      {method.label[language] || method.label.fr}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Search and Tabs */}
            <div className="py-3 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'fr' ? 'Rechercher un prisonnier...' : 'Search prisoner...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="pending" className="gap-2">
                    <Clock className="w-4 h-4" />
                    {language === 'fr' ? 'En attente' : 'Pending'} ({pendingCount})
                  </TabsTrigger>
                  <TabsTrigger value="present" className="gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Présents' : 'Present'} ({presentCount})
                  </TabsTrigger>
                  <TabsTrigger value="absent" className="gap-2">
                    <XCircle className="w-4 h-4" />
                    {language === 'fr' ? 'Absents' : 'Absent'} ({absentCount})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Prisoners List */}
            <div className="flex-1 overflow-y-auto">
              {getPrisonersByStatus(activeTab).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {language === 'fr' ? 'Aucun prisonnier dans cette catégorie' : 'No prisoners in this category'}
                </div>
              ) : (
                <div className="space-y-2">
                  {getPrisonersByStatus(activeTab).map((prisoner) => (
                    <div 
                      key={prisoner.prisoner_id}
                      className={`p-3 rounded-lg border flex items-center justify-between gap-4 transition-colors ${
                        currentPrisoner?.prisoner_id === prisoner.prisoner_id ? 'border-primary bg-primary/5' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{prisoner.name}</p>
                          <p className="text-xs text-muted-foreground">{prisoner.prisoner_id}</p>
                        </div>
                      </div>

                      {prisoner.status === 'PENDING' ? (
                        <div className="flex items-center gap-2">
                          {/* Biometric verification buttons */}
                          {verificationMethod !== 'VISUAL' && (
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCurrentPrisoner(prisoner);
                                  verifyBiometric(prisoner.prisoner_id, 'FINGERPRINT');
                                }}
                                disabled={biometricScanning}
                                className="gap-1"
                              >
                                <Fingerprint className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setCurrentPrisoner(prisoner);
                                  verifyBiometric(prisoner.prisoner_id, 'RETINAL');
                                }}
                                disabled={biometricScanning}
                                className="gap-1"
                              >
                                <ScanLine className="w-4 h-4" />
                              </Button>
                            </div>
                          )}

                          {/* Status buttons */}
                          <Select onValueChange={(status) => recordResponse(prisoner.prisoner_id, status)}>
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder={language === 'fr' ? 'Statut' : 'Status'} />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLL_CALL_STATUSES.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  <div className="flex items-center gap-2">
                                    <status.icon className={`w-4 h-4 ${status.color.split(' ')[0]}`} />
                                    {status.label[language] || status.label.fr}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Quick present button */}
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => recordResponse(prisoner.prisoner_id, 'PRESENT')}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={ROLL_CALL_STATUSES.find(s => s.value === prisoner.status)?.color || ''}
                          >
                            {ROLL_CALL_STATUSES.find(s => s.value === prisoner.status)?.label[language] || prisoner.status}
                          </Badge>
                          {prisoner.verification_method && prisoner.verification_method !== 'VISUAL' && (
                            <Badge variant="outline" className="gap-1">
                              {prisoner.verification_method === 'FINGERPRINT' && <Fingerprint className="w-3 h-3" />}
                              {prisoner.verification_method === 'RETINAL' && <ScanLine className="w-3 h-3" />}
                              {prisoner.verification_method === 'BOTH_BIOMETRIC' && <UserCheck className="w-3 h-3" />}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Biometric Scanning Overlay */}
            {biometricScanning && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-50">
                <div className="text-center p-8 bg-card rounded-lg shadow-lg">
                  <div className="relative w-32 h-32 mx-auto mb-4">
                    <div className="absolute inset-0 border-4 border-primary rounded-full animate-ping opacity-20" />
                    <div className="absolute inset-2 border-4 border-primary/50 rounded-full animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ScanLine className="w-16 h-16 text-primary animate-pulse" />
                    </div>
                  </div>
                  <p className="font-medium text-lg">
                    {language === 'fr' ? 'Scan en cours...' : 'Scanning...'}
                  </p>
                  <p className="text-muted-foreground text-sm mt-1">
                    {language === 'fr' ? 'Veuillez placer le doigt ou l\'œil sur le scanner' : 'Please place finger or eye on scanner'}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          {rollCall && (
            <Button 
              onClick={completeRollCall}
              disabled={pendingCount === rollCall.total_prisoners}
              className="gap-2"
            >
              <Square className="w-4 h-4" />
              {language === 'fr' ? 'Terminer l\'appel' : 'Complete Roll Call'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrisonerRollCall;
